import React, { useState, useRef, useEffect } from 'react';
import {
  Divider,
  Input,
  Button,
  Alert,
  Row,
  Col,
  Layout,
  Typography
} from 'antd';
import { Form } from '@ant-design/compatible';
import { Icon } from '@ant-design/compatible';
import { Link, Redirect } from 'react-router-dom';
import * as PropTypes from 'prop-types';
import ReCAPTCHA from 'react-google-recaptcha';

import './Login.scss';
import styles from './Login.module.css';
import { ReactComponent as Logo } from '~/assets/logo.svg';
import GRPC_AUTH from '~/common/grpc/AuthService';
import { rootRoutes } from '~/common/routerConfig';
import * as links from '~/common/licenseLinks';
import { AUTH_METHOD_TYPE } from '~/common/constants';
import { ReactComponent as MicroSoftLogo } from '~/assets/microsoft logo.svg';
import { ReactComponent as GoogleLogo } from '~/assets/google-logo.svg';

const { Header, Footer, Content } = Layout;
const { Text } = Typography;
const { MICROSOFT, GOOGLE } = AUTH_METHOD_TYPE;

const LOGIN_FIELDS = {
  login: 'login',
  password: 'password'
};

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY_TEST || '';

export const NormalLoginForm = ({
  intl,
  form,
  isLoading,
  onSubmit,
  error,
  messages = [],
  getExternalAuthMetadata,
}) => {
  const [validCaptcha, setValidCaptcha] = useState(false);
  const [securityCaution, setSecurityCaution] = useState(false);
  const [loginLocked, setLoginLocked] = useState(false);
  const recaptchaRef = useRef(null);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 575);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 575);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const resetState = () => {
    setValidCaptcha(false);
    setSecurityCaution(false);
    setLoginLocked(false);
  };

  const LINKS = [
    {
      name: intl.formatMessage({ id: 'btn.help' }),
      link: links.help
    },
    {
      name: intl.formatMessage({ id: 'btn.termsOfUse' }),
      link: links.termsOfUse
    }
  ];

  const handleSubmit = e => {
    e.preventDefault();
    const {
      UNAUTHENTICATED: { ACCOUNT_LOCKED, NEED_CAPTCHA }
    } = GRPC_AUTH;

    form.validateFields(async (err, values) => {
      if (err) {
        return;
      }

      const result = await onSubmit({
        login: values[LOGIN_FIELDS.login],
        password: values[LOGIN_FIELDS.password]
      });

      resetCaptcha();
      if (result.error && result.error.grpc === ACCOUNT_LOCKED) {
        resetState();
        setLoginLocked(true);
      } else if (result.error && result.error.grpc === NEED_CAPTCHA) {
        resetState();
        setSecurityCaution(true);
      }
    });
  };

  const resetCaptcha = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
      resetState();
    }
  };

  const handleRecaptchaChange = value => {
    if (value) setValidCaptcha(true);
  };

  const { getFieldDecorator } = form;
  const grpc = error ? error.grpc : null;

  if (
    grpc === GRPC_AUTH.UNAUTHENTICATED.USER_INACTIVE ||
    grpc === GRPC_AUTH.UNAUTHENTICATED.USER_DELETED
  )
    return (
      <Redirect
        to={{
          pathname: '/disabled-account',
          state: { email: form.getFieldValue(LOGIN_FIELDS.login) }
        }}
      />
    );
  else if (grpc === GRPC_AUTH.UNAUTHENTICATED.REQUIRED_CHANGE_PASS)
    return (
      <Redirect
        to={{
          pathname: rootRoutes.changePassword,
          state: { login: form.getFieldValue(LOGIN_FIELDS.login) }
        }}
      />
    );
  else if (grpc === GRPC_AUTH.UNAUTHENTICATED.USER_PASS_EXPIRED) {
    return (
      <Redirect
        to={{
          pathname: rootRoutes.changeExpiredPassword,
          state: { email: form.getFieldValue(LOGIN_FIELDS.login) }
        }}
      />
    );
  }

  const showCaptcha = securityCaution && !loginLocked && SITE_KEY.length;

  const handleMicrosoftLogin = async () => {
    if (microsoftLoading) return;
    setMicrosoftLoading(true);
    const response = await getExternalAuthMetadata({ type: MICROSOFT });
    if (response !== undefined && response.success === true) {
      const { redirectUrl } = response;
      window.location.href = redirectUrl;
    } else {
      setMicrosoftLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    if (googleLoading) return;
    setGoogleLoading(true);
    const response = await getExternalAuthMetadata({ type: GOOGLE });
    if (response !== undefined && response.success === true) {
      const { redirectUrl } = response;
      window.location.href = redirectUrl;
    } else {
      setGoogleLoading(false);
    }
  };

  const content = (
    <div className="accelaero-login-form">
      <Row>
        <Col xs={24} sm={2} md={2} lg={2} xl={5} />
        <Col xs={24} sm={9} md={9} lg={9} xl={6} className="loginTypeContainer">
          {messages.length > 0 && (
            <>
              {messages.map((message, index) => (
                <Alert key={index} message={message} type="info" showIcon />
              ))}
              <br />
            </>
          )}

          <Form
            layout="vertical"
            onSubmit={handleSubmit}
            className="login-form"
          >
            <div className={styles.h2Style}>
              <h2 className="loginTitles">
                {intl.formatMessage({ id: 'auth.login.screenTitle2' })}
              </h2>
              {error && (
                <Alert
                  message={error.message}
                  type="error"
                  showIcon
                  data-testid="next-time-login-error"
                />
              )}
            </div>

            <Form.Item
              label={intl.formatMessage({
                id: 'auth.login.usernameInputLabel'
              })}
            >
              {getFieldDecorator(LOGIN_FIELDS.login, {
                rules: [
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: 'auth.login.emptyEmailMsg'
                    })
                  }
                ]
              })(
                <Input
                  placeholder={intl.formatMessage({
                    id: 'auth.login.usernamePlaceholder'
                  })}
                  size="large"
                  type="text"
                  data-testid="next-time-login-username"
                  disabled={loginLocked}
                />
              )}
            </Form.Item>

            <Form.Item
              label={intl.formatMessage({ id: 'placeholder.password' })}
            >
              {getFieldDecorator(LOGIN_FIELDS.password, {
                rules: [
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: 'auth.login.emptyPassMsg'
                    })
                  }
                ]
              })(
                <Input
                  type="password"
                  placeholder={intl.formatMessage({
                    id: 'placeholder.password'
                  })}
                  size="large"
                  data-testid="next-time-login-password"
                  disabled={loginLocked}
                />
              )}
            </Form.Item>

            {showCaptcha && (
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={SITE_KEY}
                onChange={handleRecaptchaChange}
              />
            )}

            <Form.Item>
              <Row justify="space-between">
                <Col
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  className="loginFormButtonContainer"
                >
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="login-form-button"
                    size="large"
                    loading={isLoading}
                    data-testid="next-time-login-submit"
                    disabled={loginLocked || (securityCaution && !validCaptcha)}
                  >
                    {intl.formatMessage({ id: 'auth.login.screenTitle' })}
                  </Button>
                </Col>
                <Col
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  className="authLoginResetLinkContainer"
                >
                  <Link to={rootRoutes.requestResetPassword}>
                    {intl.formatMessage({ id: 'auth.login.resetLink' })}
                  </Link>
                </Col>
              </Row>
            </Form.Item>
          </Form>
        </Col>

        <Col xs={24} sm={2} md={2} lg={2} xl={2} className="loginDivider">
          <Row
            style={{
              height: 'auto',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Col
              xs={9}
              sm={24}
              md={24}
              lg={24}
              xl={24}
              className="firstDivider"
            >
              <Divider
                className="orDivider"
                type={isSmallScreen ? 'horizontal' : 'vertical'}
              />
            </Col>
            <Col xs={6} sm={24} md={24} lg={24} xl={24} className="orTextCol">
              <span className="orTextContainer">OR</span>
            </Col>
            <Col
              xs={9}
              sm={24}
              md={24}
              lg={24}
              xl={24}
              className="secondDivider"
            >
              <Divider
                className="orDivider"
                type={isSmallScreen ? 'horizontal' : 'vertical'}
              />
            </Col>
          </Row>
        </Col>

        <Col xs={24} sm={9} md={9} lg={9} xl={6} className="loginTypeContainer">
          <div className={styles.h2Style}>
            <h2 className="loginTitles">
              {intl.formatMessage({ id: 'auth.login.otherSignInOptions' })}
            </h2>

            <p style={{ marginTop: 16, color: 'rgba(0, 0, 0, 0.68)' }}>
              {intl.formatMessage({
                id: 'auth.login.signInUsing365MsjOrGoogle'
              })}
            </p>

            <div>
              <Button
                type="primary"
                size="large"
                className="microSoftButtonContainer"
                onClick={handleMicrosoftLogin}
                loading={microsoftLoading}
              >
            <span>
              {intl.formatMessage({id: 'auth.login.signInUsing'})}
            </span>
                <span className="microsoftLogoSpan">
              <MicroSoftLogo/>
            </span>
              </Button>
            </div>
          </div>
          <div className={styles.h2Style}>
            <div>
              <Button
                type="primary"
                size="large"
                className="googleButtonContainer"
                onClick={handleGoogleLogin}
                loading={googleLoading}
              >
            <span className="textSpan">
              {intl.formatMessage({id: 'auth.login.signInUsing'})}
            </span>
                <span className="googleLogoSpan">
              <GoogleLogo/>
            </span>
              </Button>
            </div>
          </div>
          </Col>
          <Col xs={24} sm={2} md={2} lg={2} xl={5}/>
        </Row>
      </div>

  );

  return (
    <Layout className={styles.layout}>
      <Header className={styles.header} />
      <Content className={styles.content}>
        <div className={styles.logoContainer}>
          <Logo />
        </div>
        {content}
      </Content>
      <Footer className={styles.layoutFooter}>
        <div className={styles.linkContainer}>
          {LINKS.map((item, index) => (
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              key={index}
            >
              <Text strong>{item.name}</Text>
            </a>
          ))}
          <Link
            to="/privacy-policy"
            className="privacyPolicyButton"
            data-testid="main-nav-homelink"
          >
            {intl.formatMessage({ id: 'btn.privacyPolicy' })}
          </Link>
        </div>
        <div>
          <Icon type="copyright" /> {new Date().getFullYear()}{' '}
          {intl.formatMessage({ id: 'app.accelaero' })}
        </div>
      </Footer>
    </Layout>
  );
};

NormalLoginForm.propTypes = {
  form: PropTypes.object,
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  getExternalAuthMetadata: PropTypes.func.isRequired,
  error: PropTypes.object,
  messages: PropTypes.arrayOf(PropTypes.string),
  intl: PropTypes.any
};

const LoginForm = Form.create({ name: 'normal_login' })(NormalLoginForm);

export default LoginForm;
